import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  CODE_PATTERN,
  UNITS,
  unitSchema,
  type NomenclatureCategoryDto,
  type NomenclatureDto,
} from '@eombor/contracts';
import { Button, Dialog, Input } from '@eombor/ui';
import { zodResolver } from '../../../shared/form/zod-resolver.js';
import { t } from '../../../shared/i18n/index.js';
import { Select } from '../../../shared/ui/Select.js';
import { unitText } from '../../../shared/i18n/labels.js';

/**
 * Forma sxemasi `createNomenclatureSchema` bilan BIR XIL qoidalarda, lekin
 * xabarlari maydon yonida turishga mo'ljallangan (`RoleDialog` dagi naqsh).
 *
 * `minStock` maydoni `valueAsNumber` bilan ro'yxatga olinadi, shuning uchun
 * sxema sonni ko'radi va bo'sh maydon `NaN` bo'lib xato beradi — `z.coerce`
 * ishlatilmaydi: u kirish va chiqish turlarini ajratib, `zodResolver` ning
 * turini buzadi.
 */
export const nomenclatureFormSchema = z.object({
  code: z.string().regex(CODE_PATTERN, t('catalog.errorCode')),
  name: z.string().min(2, t('catalog.errorName')).max(300),
  unit: unitSchema,
  /** Bo'sh satr — "kategoriyasiz"; backendga `null` bo'lib ketadi. */
  categoryId: z.string(),
  minStock: z
    .number({ invalid_type_error: t('catalog.errorMinStock') })
    .nonnegative(t('catalog.errorMinStock'))
    .max(1_000_000_000),
  isActive: z.boolean(),
});

export type NomenclatureFormValues = z.infer<typeof nomenclatureFormSchema>;

export interface NomenclatureDialogProps {
  open: boolean;
  onClose: () => void;
  /** Berilsa — tahrirlash rejimi: kod ham, BIRLIK ham qulflanadi. */
  nomenclature?: NomenclatureDto | null;
  categories: NomenclatureCategoryDto[];
  onSubmit: (values: NomenclatureFormValues) => void;
  pending?: boolean;
  /** Serverdan kelgan xato (masalan, kod band) — forma tepasida ko'rinadi. */
  error?: string | null;
}

const EMPTY: NomenclatureFormValues = {
  code: '',
  name: '',
  unit: 'DONA',
  categoryId: '',
  minStock: 0,
  isActive: true,
};

/* Ro'yxat CHIZILGANDA yasaladi, modul yuklanganda emas: birlik yorlig'i
   endi tarjimadan keladi va til almashganda u ham almashishi kerak. */
const unitOptions = (): Array<{ value: string; label: string }> =>
  UNITS.map((unit) => ({ value: unit, label: unitText(unit) }));

/**
 * Nomenklatura oynasi.
 *
 * Tahrirda BIRLIK o'zgarmaydi va bu ataylab: `120` soni `kg` dan `dona` ga
 * aylanganda mavjud partiyalar jimgina noto'g'ri o'qiladi. Backend ham uni
 * `updateNomenclatureSchema` da qabul qilmaydi — UI shu qoidani ko'rsatib
 * turadi, yashirmaydi.
 */
export const NomenclatureDialog = ({
  open,
  onClose,
  nomenclature = null,
  categories,
  onSubmit,
  pending = false,
  error = null,
}: NomenclatureDialogProps) => {
  const editing = nomenclature !== null;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NomenclatureFormValues>({
    resolver: zodResolver(nomenclatureFormSchema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!open) return;
    reset(
      nomenclature === null
        ? EMPTY
        : {
            code: nomenclature.code,
            name: nomenclature.name,
            unit: nomenclature.unit,
            categoryId: nomenclature.categoryId ?? '',
            minStock: nomenclature.minStock,
            isActive: nomenclature.isActive,
          },
    );
  }, [open, nomenclature, reset]);

  const submit = handleSubmit((values) => onSubmit(values));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? t('catalog.dialogEditTitle') : t('catalog.dialogCreateTitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('catalog.cancel')}
          </Button>
          <Button loading={pending} onClick={() => void submit()}>
            {editing ? t('catalog.save') : t('catalog.create')}
          </Button>
        </>
      }
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        className="flex flex-col gap-4"
      >
        {error !== null ? (
          <p
            role="alert"
            className="rounded-[var(--radius-sm)] border border-danger/25 bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {error}
          </p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('catalog.fieldCode')}
            mono
            autoComplete="off"
            spellCheck={false}
            disabled={editing}
            hint={editing ? t('catalog.codeLocked') : t('catalog.fieldCodeHint')}
            error={errors.code?.message}
            {...register('code')}
          />

          {/* `register` EMAS, `Controller`: `Select` endi xom `<select>`
              emas va unda `react-hook-form` o'qiydigan DOM qiymati yo'q.
              Qoidalar, xabarlar va yuborish tartibi o'zgarmadi — faqat
              bog'lanish usuli. */}
          <Controller
            control={control}
            name="unit"
            render={({ field }) => (
              <Select
                label={t('catalog.fieldUnit')}
                options={unitOptions()}
                disabled={editing}
                hint={editing ? t('catalog.unitLocked') : undefined}
                error={errors.unit?.message}
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
              />
            )}
          />
        </div>

        <Input
          label={t('catalog.fieldName')}
          autoComplete="off"
          error={errors.name?.message}
          {...register('name')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Controller
            control={control}
            name="categoryId"
            render={({ field }) => (
              <Select
                label={t('catalog.fieldCategory')}
                placeholder={t('catalog.categoryNone')}
                options={categories.map((category) => ({
                  value: category.id,
                  label: `${category.code} — ${category.name}`,
                }))}
                error={errors.categoryId?.message}
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
              />
            )}
          />

          <Input
            label={t('catalog.fieldMinStock')}
            mono
            type="number"
            min={0}
            step="any"
            inputMode="decimal"
            hint={t('catalog.fieldMinStockHint')}
            error={errors.minStock?.message}
            {...register('minStock', { valueAsNumber: true })}
          />
        </div>

        {editing ? (
          <label className="flex items-center gap-2 text-sm text-text">
            <input
              type="checkbox"
              className="size-4 accent-[var(--color-accent)]"
              {...register('isActive')}
            />
            {t('catalog.fieldActive')}
            <span className="text-xs text-text-faint">{t('catalog.fieldActiveHint')}</span>
          </label>
        ) : null}

        {/* `Enter` bilan yuborish — haqiqiy tugmalar pastki qatorda turadi. */}
        <button type="submit" className="sr-only" tabIndex={-1} aria-hidden>
          {t('catalog.save')}
        </button>
      </form>
    </Dialog>
  );
};
